import { EditorToolbar, RICH_TEXT_HEADING_LEVELS } from '@/components/ui/editor-toolbar';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';

interface VacancyDescriptionEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export function VacancyDescriptionEditor({
  content,
  onChange,
  placeholder = 'Describe the role, responsibilities, requirements...',
}: VacancyDescriptionEditorProps) {
  const editor = useEditor({
    extensions: [
      // StarterKit v3 bundles Link; disable it so the configured Link below is
      // the only `link` extension (avoids TipTap's duplicate-extension warning).
      StarterKit.configure({
        heading: { levels: [...RICH_TEXT_HEADING_LEVELS] },
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-primary underline' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: content || '',
    editable: true,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[300px] p-4 text-sm',
      },
    },
  });

  // Sync external content changes (e.g. template applied)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== content) {
      editor.commands.setContent(content || '', { emitUpdate: false });
    }
  }, [content, editor]);

  return (
    <div className="rounded-lg border border-border bg-background overflow-hidden">
      <EditorToolbar editor={editor} headingLevels={RICH_TEXT_HEADING_LEVELS} />
      <div className="max-h-[600px] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
